import React, { useCallback } from 'react';

import { css } from '@emotion/react';
import { spacing } from '@rtkwlf/fenrir-react';
import { useTranslation } from 'react-i18next';
import { useMatch, useNavigate } from 'react-router-dom';

import { AppBar } from '@packages/components/app-bar';
import { SettingsMenu } from '@packages/components/menu/settings-menu';
import { OrganizationDropdown } from '@packages/components/organization-dropdown';
import { useOrganizationContext } from '@packages/legacy/organization-context';
import { useFeatureFlag } from '@packages/platform/feature-flags';
import {
  DATA_FLOW_TYPE_ROUTE,
  LOG_SEARCH_ROUTE,
  LOG_SEARCH_ROUTE_SINGLE_SEARCH,
  TELEMETRY_EDIT_SCAN_SCHEDULE_ROUTE,
  TELEMETRY_SCAN_SCHEDULES_ROUTE,
  TELEMETRY_SCANNERS_CONFIG_ROUTE,
  TELEMETRY_SCANNERS_ROUTE,
  TICKET_DETAILS_ROUTE,
  TICKETS_ROUTE,
} from 'ae-constants/routes.constants';

import { DashboardUserMenu } from '../dashboard-user-menu';
import { HelpMenu } from '../help-menu/help-menu';

import { AppSwitcher } from './app-switcher/app-switcher';
import type { FeatureRelease } from './types';
import { WhatsNew } from './whats-new/whats-new';

export interface DashboardAppBarProps {
  // TODO: Remove when the unavailable route is fixed.
  showHelpMenu?: boolean;
  showOrganizationSwitcher?: boolean;
  featureReleases?: FeatureRelease[];
}

export const DashboardAppBar: React.FC<DashboardAppBarProps> = ({
  showHelpMenu = true,
  showOrganizationSwitcher = true,
  featureReleases,
}) => {
  const { t } = useTranslation();
  const [whatsNewFF] = useFeatureFlag('WHATS_NEW');

  const { selectedOrganization } = useOrganizationContext();
  const ticketRouterMatch = useMatch(TICKET_DETAILS_ROUTE);
  const telemetryScannerRouteMatch = useMatch(TELEMETRY_SCANNERS_CONFIG_ROUTE);
  const telemetryScanScheduleRouteMatch = useMatch(
    TELEMETRY_EDIT_SCAN_SCHEDULE_ROUTE,
  );
  const rawLogSearchSingleSearchRouteMatch = useMatch(
    LOG_SEARCH_ROUTE_SINGLE_SEARCH,
  );
  const dataFlowRouteMatch = useMatch(DATA_FLOW_TYPE_ROUTE);

  const navigate = useNavigate();

  // TODO: Refactor the following approach (it currently produces a console warning)
  const organizationSwitchCallback = useCallback(() => {
    if (ticketRouterMatch?.pathname) {
      navigate(TICKETS_ROUTE);
    } else if (telemetryScannerRouteMatch?.pathname) {
      navigate(TELEMETRY_SCANNERS_ROUTE);
    } else if (telemetryScanScheduleRouteMatch?.pathname) {
      navigate(TELEMETRY_SCAN_SCHEDULES_ROUTE);
    } else if (rawLogSearchSingleSearchRouteMatch?.pathname) {
      navigate(LOG_SEARCH_ROUTE);
    } else if (dataFlowRouteMatch?.pathname) {
      // must clear org specific query params that store devices, sensors, etc for filters
      navigate(dataFlowRouteMatch?.pathname);
    }
  }, [
    ticketRouterMatch?.pathname,
    telemetryScannerRouteMatch?.pathname,
    telemetryScanScheduleRouteMatch?.pathname,
    rawLogSearchSingleSearchRouteMatch?.pathname,
    dataFlowRouteMatch?.pathname,
    navigate,
  ]);

  return (
    <>
      {/* AppBar only for prints */}
      <div
        css={css({
          display: 'none',
          '@media print': {
            display: 'block',
            marginTop: spacing.small,
          },
        })}
      >
        <AppBar
          title={`${t('appBar:appName')} | ${selectedOrganization.name}`}
          isForPrint
        />
      </div>

      {/* AppBar only for screens */}
      <div
        css={css({
          '@media print': {
            display: 'none',
          },
        })}
      >
        <AppBar
          leftSection={<AppSwitcher />}
          title={t('appBar:appName')}
          rightSection={
            <>
              {whatsNewFF && featureReleases !== undefined && (
                <WhatsNew featureReleases={featureReleases} />
              )}
              {showHelpMenu && <HelpMenu />}
              <SettingsMenu />
              <DashboardUserMenu />
            </>
          }
        >
          {showOrganizationSwitcher && (
            <OrganizationDropdown
              organizationSwitchCallback={organizationSwitchCallback}
            />
          )}
        </AppBar>
      </div>
    </>
  );
};
